import { IWorklistToolInternalState } from "../shared/state/initial-state";
import { getIntTotalVol, getIntVolRemaining } from "./NucleofectionToolHelpers";
import {
  CompoundPlateInfoState,
  DestPlateInfoState,
  IntermediatePlateInfoState,
  NucleofectionPlateInfoState,
  SourcePlateInfoState,
  WorklistValuesState,
} from "./state";
import {
  INucleofectionInternalState,
  INucleofectionMethodSettings,
} from "./state/initial-state";

export const getNucleofectionRequiredWorklistWarnings = (
  compoundPlateInfo: CompoundPlateInfoState[],
  sourcePlateInfo: SourcePlateInfoState[],
  nucPlateInfo: NucleofectionPlateInfoState[],
  intPlateInfo: IntermediatePlateInfoState[],
  destPlateInfo: DestPlateInfoState[],
  worklistValues: WorklistValuesState,
  internalState: INucleofectionInternalState,
  worklistToolState: IWorklistToolInternalState
) => {
  let warnings: string[] = [];
  const methodSettings = internalState.methodSettings;
  const generalErrors = Object.values(worklistToolState.errorMessages).filter(
    (message) => message !== ""
  );
  const requiredMessages = checkForRequiredMethodSettings(methodSettings);

  const intTotalVol = getIntTotalVol(
    nucPlateInfo[0].topUpVol,
    nucPlateInfo[0].washVol,
    nucPlateInfo[0].numberOfWashes
  );

  warnings = [...generalErrors, ...requiredMessages];

  if (sourcePlateInfo[0].labwareTypeCode === "") {
    warnings.push("Upload Source Plate");
  } else if (compoundPlateInfo[0].labwareTypeCode === "") {
    warnings.push("Compound Plate Type must be selected");
  } else if (nucPlateInfo[0].labwareTypeCode === "") {
    warnings.push("Upload Nucleofection Plate");
  } else if (intPlateInfo[0].labwareTypeCode === "") {
    warnings.push("Intermediate Plate Type must be selected");
  } else if (destPlateInfo.some((e) => e.labwareTypeCode === "")) {
    warnings.push("Destination Plate Type(s) must be selected");
  } else if (destPlateInfo.some((e) => e.plateBarcode === "")) {
    warnings.push("Missing Dest Plate Barcode(s)");
  }

  if (nucPlateInfo[0].numberOfWashes !== 0) {
    if (nucPlateInfo[0].numberOfWashes > 3) {
      warnings.push("Wash Cycles cannot exceed 3");
    } else if (nucPlateInfo[0].washVol === 0) {
      warnings.push("Wash Vol is Required");
    } else if (
      Number(nucPlateInfo[0].washVol) < nucPlateInfo[0].minOperatingVol ||
      Number(nucPlateInfo[0].washVol) >
        nucPlateInfo[0].operatingVol - nucPlateInfo[0].minOperatingVol
    ) {
      warnings.push(
        `Wash Vol must be between ${nucPlateInfo[0].minOperatingVol} and ${
          nucPlateInfo[0].operatingVol - nucPlateInfo[0].minOperatingVol
        }`
      );
    }
  }

  if (
    intPlateInfo[0].rows.length > 0 &&
    intTotalVol > intPlateInfo[0].operatingVol
  ) {
    warnings.push(
      "Intermediate Plate Total Volume cannot exceed Operating Volume"
    );
  }

  for (const wellMapping of worklistValues.intToDest) {
    const minVol = destPlateInfo[wellMapping.destPlateIndex].minOperatingVol;
    const maxVol = destPlateInfo[wellMapping.destPlateIndex].operatingVol;

    if (wellMapping.transferVol === "") {
      warnings.push("You must set a transfer volume for all Destination wells");
      break;
    } else if (
      Number(wellMapping.transferVol) < minVol ||
      Number(wellMapping.transferVol) > maxVol
    ) {
      warnings.push(
        `Transfer volume must be between ${minVol} and ${maxVol} for wells in Dest Plate: ${
          destPlateInfo[wellMapping.destPlateIndex].plateBarcode
        }`
      );
      break;
    }
  }

  for (const wellMapping of worklistValues.intToDest) {
    const intVolRemaining = getIntVolRemaining(
      wellMapping.sourceWellId,
      intTotalVol,
      worklistValues.intToDest
    );

    if (intVolRemaining < 0) {
      warnings.push(
        "You're attempting to transfer more volume than is available. Increase Int Plate numWashes/washVol or reduce number of IntToDest transfers."
      );
      break;
    }
  }

  if (
    nucPlateInfo[0].labwareTypeCode !== "" &&
    worklistValues.sourceToNuc.length === 0
  ) {
    warnings.push("Add wells to Nuc Plate");
  }

  const destPlatesWithCells = new Set(
    worklistValues.intToDest.map((m) => m.destPlateIndex)
  );

  const destPlateWithNoMappings = destPlateInfo.some(
    (destPlate) =>
      destPlate.labwareTypeCode !== "" &&
      !destPlatesWithCells.has(destPlate.index)
  );

  if (destPlateWithNoMappings) {
    warnings.push("You have an empty Dest Plate. Add cells or remove plate.");
  }

  const srcPlateRunCode = sourcePlateInfo[0].plateBarcode.split("_")[0];
  const intPlateRunCode = intPlateInfo[0].plateBarcode.split("_")[0];
  const destPlateRunCodes = destPlateInfo
    .map((destPlate) => destPlate.plateBarcode.split("_")[0])
    .filter((code) => code);

  if (
    intPlateInfo[0].plateBarcode !== "" &&
    intPlateRunCode !== srcPlateRunCode
  ) {
    warnings.push(
      `Invalid Intermediate Barcode. Run ${intPlateRunCode} does not match source plate: ${srcPlateRunCode}`
    );
  }

  if (
    destPlateRunCodes.length > 0 &&
    destPlateRunCodes.some((destCode) => destCode !== srcPlateRunCode)
  ) {
    warnings.push(
      `Invalid Dest Barcode. The Run on at least one dest plate does not match source plate: ${srcPlateRunCode}`
    );
  }

  return warnings;
};

export const getNucleofectionOptionalWorklistWarnings = (
  nucPlateInfo: NucleofectionPlateInfoState[],
  worklistValues: WorklistValuesState,
  isNucPlateAlreadyReserved: boolean
) => {
  const optionalWarnings = [];

  const intTotalVol = getIntTotalVol(
    nucPlateInfo[0].topUpVol,
    nucPlateInfo[0].washVol,
    nucPlateInfo[0].numberOfWashes
  );

  if (
    worklistValues.intToDest.some((wellMapping) => {
      const intVolRemaining = getIntVolRemaining(
        wellMapping.sourceWellId,
        intTotalVol,
        worklistValues.intToDest
      );
      return intVolRemaining >= 100;
    })
  ) {
    optionalWarnings.push("You've left at least 100μl in int plate well(s).");
  }

  if (isNucPlateAlreadyReserved) {
    optionalWarnings.push(
      `Nuc Plate ${nucPlateInfo[0].plateBarcode} is in another active reservation.`
    );
  }

  return optionalWarnings;
};

const checkForRequiredMethodSettings = (
  methodSettings: INucleofectionMethodSettings
) => {
  const methodSettingDisplayValues: Map<
    keyof INucleofectionMethodSettings,
    string
  > = new Map([
    ["selectedSystem", "Selected System"],
    ["destTransferStrategy", "Destination Plates Transfer Strategy"],
    ["destSampleType", "Destination Plates Sample Type"],
  ]);

  const requiredFieldMissing = (key: keyof INucleofectionMethodSettings) =>
    methodSettings[key] === undefined ||
    ["", "-1", "0"].includes(methodSettings[key]!.toString());

  const requiredMessages = [...methodSettingDisplayValues.keys()]
    .filter((key) => requiredFieldMissing(key))
    .map((key) => `${methodSettingDisplayValues.get(key)} is a required field`);

  return [...requiredMessages];
};
